import { z } from 'zod';

export const MessageKindSchema = z.enum([
  'FIRST_CONTACT',
  'WELCOME',
  'GUEST_FORM',
  'ACCESS',
  'PAYMENT_LINK',
  'REVIEW',
  'CUSTOM',
]);

export const MessagePrioritySchema = z.enum(['urgent', 'high', 'normal', 'low']);
export const MessageStatusSchema = z.enum(['pending', 'done', 'snoozed']);

export const TemplateVariableSchema = z.object({
  key: z.string(),
  required: z.boolean(),
  source: z.string(),
});

export const MessageTemplateSchema = z.object({
  id: z.string(),
  kind: MessageKindSchema,
  language: z.string(),
  name: z.string(),
  content: z.string(),
  variables: z.array(TemplateVariableSchema),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdById: z.string().optional(),
});

export const MessagingTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  category: z.literal('messaging'),
  priority: MessagePrioritySchema,
  status: MessageStatusSchema,
  messageKind: MessageKindSchema,
  templateId: z.string().optional(),
  templateSnapshot: z.string().optional(),
  sentMessageSnapshot: z.string().optional(),
  whatsappLink: z.string().optional(),
  recipientPhone: z.string().optional(),
  recipientLanguage: z.string().optional(),
  sentAt: z.string().optional(),
  sentById: z.string().optional(),
  sentByName: z.string().optional(),
  messageVariables: z.record(z.string()).optional(),
  reservationId: z.string().optional(),
  clientId: z.string().optional(),
  dueDate: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ComposeResultSchema = z.object({
  text: z.string(),
  whatsappLink: z.string(),
  phoneValid: z.boolean(),
  hasWhatsappKnown: z.boolean().nullable(),
  warnings: z.array(z.string()),
});

export const CreateMessagingTaskRequestSchema = z.object({
  reservationId: z.string(),
  messageKind: MessageKindSchema,
  manualVariables: z.record(z.string()).optional(),
  customText: z.string().optional(),
  language: z.string().optional(),
});

export const CreateMessagingTaskResponseSchema = z.object({
  taskId: z.string(),
  text: z.string(),
  whatsappLink: z.string(),
  warnings: z.array(z.string()),
  phoneValid: z.boolean(),
  hasWhatsappKnown: z.boolean().nullable(),
});

export const MarkSentRequestSchema = z.object({
  sentMessageSnapshot: z.string(),
  autoConfirmed: z.boolean().optional(),
});

export const MessagingConfigSchema = z.object({
  hotelPhone: z.string(),
  hotelName: z.string(),
  wifi: z.string(),
  whatsappLinkFormat: z.enum(['universal', 'desktop', 'mobile']),
  enabledKinds: z.record(z.boolean()),
  autoTaskCreation: z.record(z.object({
    enabled: z.boolean(),
    trigger: z.string(),
    daysBefore: z.number().optional(),
    daysAfter: z.number().optional(),
    channels: z.array(z.string()).optional(),
  })),
});

export const CommunicationLogSchema = z.object({
  id: z.string(),
  clientId: z.string().optional(),
  reservationId: z.string().optional(),
  phoneNumber: z.string().optional(),
  source: z.enum(['WHATSAPP_PASTE', 'EMAIL', 'OTHER']),
  rawContent: z.string(),
  parsedMessages: z.array(z.object({
    timestamp: z.string(),
    sender: z.string(),
    content: z.string(),
    isHost: z.boolean(),
  })),
  summary: z.string(),
  loggedById: z.string(),
  loggedByName: z.string().optional(),
  loggedAt: z.string(),
});

// Types inferred from schemas
export type MessageKind = z.infer<typeof MessageKindSchema>;
export type MessageTemplate = z.infer<typeof MessageTemplateSchema>;
export type MessagingTask = z.infer<typeof MessagingTaskSchema>;
export type ComposeResult = z.infer<typeof ComposeResultSchema>;
export type CreateMessagingTaskRequest = z.infer<typeof CreateMessagingTaskRequestSchema>;
export type CreateMessagingTaskResponse = z.infer<typeof CreateMessagingTaskResponseSchema>;
export type MarkSentRequest = z.infer<typeof MarkSentRequestSchema>;
export type MessagingConfig = z.infer<typeof MessagingConfigSchema>;
export type CommunicationLog = z.infer<typeof CommunicationLogSchema>;
